export type CssValidationResult = {
  ok: boolean
  message?: string
}

const BLOCKED_PATTERNS: Array<{ re: RegExp; message: string }> = [
  { re: /@import\b/i, message: '禁止使用 @import，請改用內建資源。' },
  { re: /expression\s*\(/i, message: '禁止使用 expression()。' },
  { re: /javascript\s*:/i, message: '禁止使用 javascript: URL。' },
]

const GLOBAL_SELECTOR_REGEX = /^(html|body|:root|\*)(\b|[\s>+~:[.#]|$)/i

function runBlockedPatternChecks(css: string): CssValidationResult | null {
  for (const rule of BLOCKED_PATTERNS) {
    if (rule.re.test(css)) return { ok: false, message: rule.message }
  }
  return null
}

function hasBalancedBraces(css: string): boolean {
  let depth = 0
  for (const ch of css) {
    if (ch === '{') depth += 1
    else if (ch === '}') {
      depth -= 1
      if (depth < 0) return false
    }
  }
  return depth === 0
}

function collectSelectors(css: string): string[] {
  const selectors: string[] = []
  const selectorBlockRegex = /([^{}]+)\{/g
  let m: RegExpExecArray | null = null
  while ((m = selectorBlockRegex.exec(css)) !== null) {
    const selectorBlock = m[1].trim()
    if (!selectorBlock || selectorBlock.startsWith('@')) continue
    const splitSelectors = selectorBlock.split(',').map((s) => s.trim()).filter(Boolean)
    selectors.push(...splitSelectors)
  }
  return selectors
}

export function validateCustomCss(css: string): CssValidationResult {
  const blocked = runBlockedPatternChecks(css)
  if (blocked) return blocked
  if (!hasBalancedBraces(css)) {
    return { ok: false, message: 'CSS 語法錯誤：大括號不成對。' }
  }

  const allowedScopeHints = [
    '.appfactory-device-modal',
    '.smart-home-device-modal',
    '.smart-home-dashboard-template',
    '.device-type-modal-preview-viewport-402',
    '.appfactory-login-page-viewport',
  ]

  const globalSelectors: string[] = []
  for (const selector of collectSelectors(css)) {
    const isScopedByKnownRoot = allowedScopeHints.some((hint) => selector.includes(hint))
    if (isScopedByKnownRoot) continue
    if (GLOBAL_SELECTOR_REGEX.test(selector)) {
      globalSelectors.push(selector)
    }
  }

  if (globalSelectors.length > 0) {
    const sample = globalSelectors.slice(0, 3).join(', ')
    return {
      ok: false,
      message: `請避免全域 selector（${sample}）。請加上容器 scope，例如 .appfactory-device-modal ...`,
    }
  }

  return { ok: true }
}

export function validateItemCss(css: string): CssValidationResult {
  const blocked = runBlockedPatternChecks(css)
  if (blocked) return blocked
  if (!hasBalancedBraces(css)) {
    return { ok: false, message: 'Item CSS 語法錯誤：大括號不成對。' }
  }

  if (/@(?!import\b)/i.test(css)) {
    return { ok: false, message: 'Item CSS 暫不支援 @ 規則（例如 @media / @keyframes）。' }
  }

  const globalSelectors: string[] = []
  for (const selector of collectSelectors(css)) {
    const normalized = selector.startsWith('&') ? selector.slice(1).trim() : selector
    if (GLOBAL_SELECTOR_REGEX.test(normalized)) {
      globalSelectors.push(selector)
    }
  }

  if (globalSelectors.length > 0) {
    const sample = globalSelectors.slice(0, 3).join(', ')
    return {
      ok: false,
      message: `Item CSS 禁止全域 selector（${sample}）。`,
    }
  }

  return { ok: true }
}

function escapeAttrValue(value: string): string {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function buildItemSelectors(modalRootSelector: string, itemKey: string): string[] {
  const escapedItemKey = escapeAttrValue(itemKey)
  const selectors = new Set<string>([
    `${modalRootSelector} [data-item-key="${escapedItemKey}"]`,
  ])
  const [rawKind, ...idParts] = String(itemKey || '').split(':')
  const rawId = idParts.join(':').trim()
  const kind = String(rawKind || '').trim()
  if (kind && rawId) {
    const escapedKind = escapeAttrValue(kind)
    const escapedId = escapeAttrValue(rawId)
    selectors.add(
      `${modalRootSelector} [data-item-kind="${escapedKind}"][data-item-id="${escapedId}"]`
    )
    // 舊資料容錯：允許大小寫不同（例如 trait:onoff vs trait:OnOff）
    selectors.add(
      `${modalRootSelector} [data-item-kind="${escapedKind}" i][data-item-id="${escapedId}" i]`
    )
  }
  return Array.from(selectors)
}

function scopeSelector(baseSelector: string, itemRootSelector: string): string {
  const selector = baseSelector.trim()
  if (!selector) return ''
  if (selector.startsWith('&')) return `${itemRootSelector}${selector.slice(1)}`
  return `${itemRootSelector} ${selector}`
}

function scopeItemCss(itemCss: string, itemRootSelector: string): string {
  const source = String(itemCss || '').trim()
  if (!source) return ''

  // 支援「純 declaration」輸入：直接套到該 item root
  if (!source.includes('{')) return `${itemRootSelector} { ${source} }`

  const scopedBlocks: string[] = []
  const blockRegex = /([^{}]+)\{([^{}]*)\}/g
  let m: RegExpExecArray | null = null
  while ((m = blockRegex.exec(source)) !== null) {
    const selectorBlock = m[1].trim()
    const body = m[2]
    if (!selectorBlock || selectorBlock.startsWith('@')) continue
    const selectors = selectorBlock
      .split(',')
      .map((s) => scopeSelector(s, itemRootSelector))
      .filter(Boolean)
    if (selectors.length === 0) continue
    scopedBlocks.push(`${selectors.join(', ')} {${body}}`)
  }

  if (scopedBlocks.length === 0) {
    return `${itemRootSelector} { ${source} }`
  }
  return scopedBlocks.join('\n')
}

function resolveItemSlotSelector(itemSelector: string, slotKey: string): string {
  const rawSlot = String(slotKey || '').trim()
  const normalized = rawSlot
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
  const canonicalSlotMap: Record<string, string> = {
    powerlabel: 'powerLabel',
    powerstatus: 'powerStatus',
    powericon: 'powerIcon',
    powerbuttonsurface: 'powerButtonSurface',
    powertrack: 'powerTrack',
    powerthumb: 'powerThumb',
    opencloseicon: 'openCloseIcon',
    openclosetitle: 'openCloseTitle',
    openclosevalue: 'openCloseValue',
    opencloselabels: 'openCloseLabels',
    openclosecontrol: 'openCloseControl',
    alarmsurface: 'alarmSurface',
    alarmicon: 'alarmIcon',
    alarmname: 'alarmName',
    alarmstatus: 'alarmStatus',
    temperaturearc: 'temperatureArc',
    temperaturecircle: 'temperatureCircle',
    temperaturevalue: 'temperatureValue',
    temperatureunit: 'temperatureUnit',
    temperaturetitle: 'temperatureTitle',
    temperatureheadericon: 'temperatureHeaderIcon',
    plusbtn: 'plusBtn',
    minusbtn: 'minusBtn',
    plusbtnicon: 'plusBtnIcon',
    minusbtnicon: 'minusBtnIcon',
    chartline: 'chartLine',
  }
  const canonicalSlot = canonicalSlotMap[normalized] || rawSlot
  if (!normalized || normalized === 'itemroot') return itemSelector
  if (normalized === 'materialicon') {
    return `${itemSelector} .material-symbols-rounded, ${itemSelector} .material-symbols-outlined`
  }
  if (normalized === 'svgicon') {
    return `${itemSelector} svg, ${itemSelector} svg *`
  }
  if (normalized === 'text') {
    return `${itemSelector} span, ${itemSelector} p, ${itemSelector} label, ${itemSelector} h1, ${itemSelector} h2, ${itemSelector} h3, ${itemSelector} h4, ${itemSelector} h5, ${itemSelector} h6, ${itemSelector} small, ${itemSelector} strong, ${itemSelector} em`
  }
  if (normalized === 'controlsurface') {
    return `${itemSelector} button, ${itemSelector} [role="button"], ${itemSelector} [role="switch"], ${itemSelector} [role="slider"], ${itemSelector} input, ${itemSelector} select, ${itemSelector} textarea`
  }
  // 舊資料容錯：slot key 可能大小寫不一致，使用 case-insensitive 屬性比對。
  return `${itemSelector} [data-css-slot="${escapeAttrValue(canonicalSlot)}" i]`
}

function normalizeCssStateToken(value: string): string {
  const token = String(value || '').trim().toLowerCase()
  if (!token) return ''
  const first = token.split(/\s+/)[0] || ''
  if (!first) return ''
  if (first === 'open' || first === 'start' || first === 'armed') return 'on'
  if (first === 'close' || first === 'closed' || first === 'stop' || first === 'disarmed') return 'off'
  if (first === 'active') return 'selected'
  if (first === 'inactive') return 'unselected'
  if (first === 'selected' || first === 'unselected' || first === 'disabled') return first
  return first === 'off' ? 'off' : 'on'
}

function shouldSkipStateScopeForSlot(slotKey: string): boolean {
  const normalized = String(slotKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
  const stateScopedSlots = new Set([
    // on/off 類
    'powerlabel',
    'powerstatus',
    'powericon',
    'powerbuttonsurface',
    'powertrack',
    'powerthumb',
    // open/close 類
    'opencloseicon',
    'openclosetitle',
    'openclosevalue',
    'opencloselabels',
    'openclosecontrol',
    // alarm 類
    'alarmicon',
    'alarmname',
    'alarmstatus',
    'alarmsurface',
  ])
  if (!normalized) return true
  // 只有明確列入 stateScopedSlots 的 slot 會加上 [data-css-state] 篩選
  return !stateScopedSlots.has(normalized)
}

export function buildScopedModalItemCss(
  modalItemCss: Record<string, string> | undefined,
  modalRootSelector: string
): string {
  if (!modalItemCss) return ''
  const blocks: string[] = []
  for (const [keyToken, rawCss] of Object.entries(modalItemCss)) {
    const css = String(rawCss || '').trim()
    if (!css) continue
    const [itemKey, slotAndStateToken] = keyToken.split('::', 2).map((token) => token?.trim() ?? '')
    if (!itemKey) continue
    const [slotKey, stateToken] = String(slotAndStateToken || '')
      .split('@', 2)
      .map((token) => token?.trim() ?? '')
    const normalizedState = normalizeCssStateToken(stateToken)
    const itemSelectors = buildItemSelectors(modalRootSelector, itemKey)
    for (const itemSelector of itemSelectors) {
      const slotSelector = resolveItemSlotSelector(itemSelector, slotKey)
      const skipStateScope = shouldSkipStateScopeForSlot(slotKey)
      const itemRootSelector =
        normalizedState && normalizedState !== 'base' && !skipStateScope
          ? `${slotSelector}[data-css-state~="${escapeAttrValue(normalizedState)}"]`
          : slotSelector
      const scoped = scopeItemCss(css, itemRootSelector)
      if (scoped) blocks.push(scoped)
    }
  }
  return blocks.join('\n')
}
